import { redirect, type LoaderFunction } from 'react-router';
import store from '../../store';
import services from '../../services';
import {
  updateCountryStateInSearch,
  updatePostCodeInSearch,
} from '../../reducers/search/searchReducer';
import { saveUpdatedSessionItem } from '../../helpers/sessionHelper';
import {
  addPersistingQueryParamsToPath,
  getPagePath,
  sanitizeInput,
} from '../../helpers/urlHelper';
import {
  setDisabledPrevButton,
  updatePage,
  updateSelectedService,
} from '../../reducers/app/appReducer';
import { viewContext } from '../middleware/context';
import type { AppView } from '../../types/app';
import { clearAvailableServices } from '../../reducers/services/servicesReducer';

const validatePostalCode = (postalCode: string): boolean => {
  const postalCodeRegexp = new RegExp(CSS_CONFIG.regExp.zipCode);
  return !!postalCode && postalCodeRegexp.test(postalCode);
};

const getStateCode = async (
  postalCode: string,
): Promise<{ isValid: boolean; stateCode?: string }> => {
  const { services: servicesConfig } = CSS_CONFIG;
  if (!servicesConfig.getServicesUsingStateCode) {
    return { isValid: true };
  }

  try {
    const postalCodeLookup =
      await services.state.getPostalCodeLocations(postalCode);
    if (postalCodeLookup.valid && postalCodeLookup.locations.length > 0) {
      const stateCode = postalCodeLookup.locations[0].state;
      return { isValid: true, stateCode };
    }
  } catch (error) {
    // Postal code lookup failed — treat as invalid
  }
  return { isValid: false };
};

const handleDirectNavigation = async (
  requestUrl: string,
  view: AppView,
): Promise<void> => {
  const url = new URL(requestUrl);
  const params = url.searchParams;
  const pathname = url.pathname;
  const operation = pathname.split('/').slice(-2, -1)[0];
  const postalCodeInURL = params.get('zip');

  if (postalCodeInURL) {
    const validPostalCode = validatePostalCode(postalCodeInURL);
    const { isValid, stateCode } = await getStateCode(postalCodeInURL);

    if (!validPostalCode || !isValid) {
      window.history.replaceState(null, '', url.pathname);
      return;
    }

    store.dispatch(updatePostCodeInSearch(postalCodeInURL || ''));
    saveUpdatedSessionItem('search', { postCode: postalCodeInURL || '' });
    if (stateCode) {
      store.dispatch(updateCountryStateInSearch(stateCode));
    }

    const serviceInURL = params.get('service');
    if (serviceInURL) {
      store.dispatch(setDisabledPrevButton('timeslots'));
      store.dispatch(
        updateSelectedService({
          serviceId: sanitizeInput(serviceInURL).toUpperCase(),
        }),
      );
      const pagePath = getPagePath(`${operation}/timeslots`, view);
      throw redirect(addPersistingQueryParamsToPath(`${pagePath}`));
    }
    store.dispatch(setDisabledPrevButton('services'));
    const pagePath = getPagePath(`${operation}/services`, view);
    throw redirect(addPersistingQueryParamsToPath(`${pagePath}`));
  }
};

const loader: LoaderFunction = async (args) => {
  const view = args.context.get(viewContext);
  await handleDirectNavigation(args.request.url, view);
  store.dispatch(updatePage('start'));
  store.dispatch(clearAvailableServices());
  store.dispatch(updateSelectedService());
};

export default loader;
